// Coupon CRUD wrapping the existing /api/admin/coupons/ endpoints.

import React, { useEffect, useState } from 'react';
import {
  createAdminCoupon,
  deleteAdminCoupon,
  listAdminCoupons,
  updateAdminCoupon,
} from '../../services/adminService';
import DataTable from '../common/DataTable';
import ConfirmDialog from '../common/ConfirmDialog';
import PortalModal, { ModalActions } from '../common/PortalModal';
import Field from '../common/Field';

const DISCOUNT_TYPES = ['flat', 'percent', 'bogo', 'free_item'];

const today = () => new Date().toISOString().slice(0, 10);
const plus = (days) => new Date(Date.now() + days * 86400000).toISOString().slice(0, 10);

const blank = {
  title: '', description: '', store_name: '',
  discount_type: 'percent', discount_value: '10',
  minimum_purchase: 0, valid_from: today(), valid_until: plus(30),
  status: 'active', max_claims: 0, image_url: '',
};

export default function AdminCoupons() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [storeFilter, setStoreFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [expiryFilter, setExpiryFilter] = useState('');   // '', 'active', 'expired'
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [showForm, setShowForm] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const reload = () => listAdminCoupons().then(d => setRows(d.coupons || [])).catch(() => {});
  useEffect(() => { reload(); }, []);

  // Distinct stores in the dataset — drives the store filter dropdown so we
  // don't ask the admin to type names exactly.
  const storeOptions = Array.from(new Set(rows.map(r => r.store_name).filter(Boolean))).sort();

  const needle = q.trim().toLowerCase();
  const visible = rows.filter(c => {
    if (needle) {
      const hay = `${c.title} ${c.description || ''} ${c.store_name || ''}`.toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    if (storeFilter && c.store_name !== storeFilter) return false;
    if (typeFilter && c.discount_type !== typeFilter) return false;
    if (expiryFilter === 'active' && c.days_left <= 0) return false;
    if (expiryFilter === 'expired' && c.days_left > 0) return false;
    return true;
  });

  const resetFilters = () => { setQ(''); setStoreFilter(''); setTypeFilter(''); setExpiryFilter(''); };
  const hasFilters = q || storeFilter || typeFilter || expiryFilter;

  const openCreate = () => { setEditing(null); setForm(blank); setShowForm(true); setError(''); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      title: c.title, description: c.description, store_name: c.store_name,
      discount_type: c.discount_type, discount_value: c.discount_value,
      minimum_purchase: c.minimum_purchase, valid_from: c.valid_from, valid_until: c.valid_until,
      status: c.status || 'active', max_claims: c.max_claims || 0, image_url: c.image_url || '',
    });
    setShowForm(true);
    setError('');
  };

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      if (editing) await updateAdminCoupon(editing.id, form);
      else await createAdminCoupon(form);
      setShowForm(false);
      reload();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    await deleteAdminCoupon(confirmDel.id);
    setConfirmDel(null);
    reload();
  };

  return (
    <>
      <div className="portal-content-header">
        <h1>Coupons</h1>
        <button type="button" className="portal-button" onClick={openCreate}>+ New coupon</button>
      </div>

      <div className="portal-filter-bar">
        <input
          type="search"
          className="portal-search"
          placeholder="Search title, description, store"
          value={q}
          onChange={e => setQ(e.target.value)}
        />
        <select className="portal-search" value={storeFilter} onChange={e => setStoreFilter(e.target.value)}>
          <option value="">All stores</option>
          {storeOptions.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="portal-search" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="">All discount types</option>
          {DISCOUNT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="portal-search" value={expiryFilter} onChange={e => setExpiryFilter(e.target.value)}>
          <option value="">All validity</option>
          <option value="active">Still valid</option>
          <option value="expired">Expired</option>
        </select>
        {hasFilters && <button type="button" className="portal-button secondary" onClick={resetFilters}>Clear</button>}
        <span className="meta">{visible.length} of {rows.length} coupons</span>
      </div>

      <DataTable
        columns={[
          { key: 'title', label: 'Title', render: c => <strong>{c.title}</strong> },
          { key: 'store_name', label: 'Store' },
          { key: 'discount', label: 'Discount', render: c => `${c.discount_value} (${c.discount_type})` },
          { key: 'valid_until', label: 'Valid until' },
          { key: 'claim_count', label: 'Claims' },
          { key: 'days_left', label: 'Days left' },
          { key: 'actions', label: '', render: c => (
            <div className="row-actions">
              <button type="button" className="portal-button secondary" onClick={() => openEdit(c)}>Edit</button>
              <button type="button" className="portal-button danger" onClick={() => setConfirmDel(c)}>Delete</button>
            </div>
          ) },
        ]}
        rows={visible}
        emptyMessage={hasFilters ? 'No coupons match the current filters.' : 'No coupons yet.'}
      />

      <PortalModal
        open={showForm}
        onClose={() => setShowForm(false)}
        title={editing ? `Edit coupon: ${editing.title}` : 'New coupon'}
      >
        {error && <div className="portal-error">{error}</div>}
        <div className="portal-form-grid">
          <Field label="Title">
            <input className="portal-search" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
          </Field>
          <Field label="Store name">
            <input className="portal-search" value={form.store_name} onChange={e => setForm({ ...form, store_name: e.target.value })} />
          </Field>
          <Field label="Discount type">
            <select className="portal-search" value={form.discount_type} onChange={e => setForm({ ...form, discount_type: e.target.value })}>
              {DISCOUNT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Discount value (e.g. 10, 25%, $5)">
            <input className="portal-search" value={form.discount_value} onChange={e => setForm({ ...form, discount_value: e.target.value })} />
          </Field>
          <Field label="Minimum purchase ($)">
            <input className="portal-search" value={form.minimum_purchase} onChange={e => setForm({ ...form, minimum_purchase: e.target.value })} />
          </Field>
          <Field label="Max claims (0 = unlimited)">
            <input className="portal-search" value={form.max_claims} onChange={e => setForm({ ...form, max_claims: e.target.value })} />
          </Field>
          <Field label="Valid from">
            <input type="date" className="portal-search" value={form.valid_from} onChange={e => setForm({ ...form, valid_from: e.target.value })} />
          </Field>
          <Field label="Valid until">
            <input type="date" className="portal-search" value={form.valid_until} onChange={e => setForm({ ...form, valid_until: e.target.value })} />
          </Field>
          <Field label="Status">
            <select className="portal-search" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
              <option value="active">active</option>
              <option value="inactive">inactive</option>
            </select>
          </Field>
          <Field label="Image URL (optional)">
            <input className="portal-search" value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} />
          </Field>
          <Field label="Description" full>
            <textarea className="portal-textarea" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
          </Field>
        </div>
        <ModalActions>
          <button type="button" className="portal-button secondary" onClick={() => setShowForm(false)}>Cancel</button>
          <button type="button" className="portal-button" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
        </ModalActions>
      </PortalModal>

      <ConfirmDialog
        open={!!confirmDel}
        title="Delete coupon?"
        message={`Delete "${confirmDel?.title}" and revoke its existing claims? This cannot be undone.`}
        confirmLabel="Delete"
        danger
        onConfirm={doDelete}
        onCancel={() => setConfirmDel(null)}
      />
    </>
  );
}
